import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, TextInput, View, TouchableOpacity, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import { isDarkTheme, getShadowThemeStyles } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';

const createStyles = (colors, isXS, isRoundedStyle, isDark, themeStyles, noPadding, catalogVariant) => StyleSheet.create({
  searchContainer: {
    padding: noPadding ? 0 : isXS ? 15 : 20,
    paddingBottom: 2,
  },
  searchWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: catalogVariant
      ? colors.solid_color_block
      : (isDark || !isRoundedStyle) ? colors.solid_color_tertiary : colors.solid_color_primary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    borderRadius: catalogVariant ? 10 : (isRoundedStyle ? 100 : 8),
    paddingLeft: isRoundedStyle ? 20 : 14,
    paddingRight: 14,
    height: catalogVariant
      ? (isXS ? 40 : 44)
      : ((isXS || !isRoundedStyle) ? 44 : 56),
    ...(isRoundedStyle ? (
      Platform.OS === 'web'
        ? { boxShadow: isDark ? themeStyles.boxShadow : 'none' }
        : {}
    ) : {}),
  },
  searchWrapperFocused: {
    borderColor: Platform.OS === 'macos' ? colors.text_color_tertiary : colors.border_color_secondary,
  },
  searchIcon: {
    marginRight: isRoundedStyle ? 15 : 10,
  },
  searchInput: {
    flex: 1,
    fontSize: isRoundedStyle ? 16 : 14,
    color: colors.text_color_primary,
    height: '100%',
    outlineStyle: 'none',
    paddingTop: 0,
  },
  clearButton: {
    marginLeft: 8,
    padding: 4,
  },
  submitButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    marginLeft: 8,
    backgroundColor: colors.accent_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
});

const PageSearchInput = ({
  value,
  onChangeText,
  placeholder = "Search...",
  style,
  icon = "magnifying-glass",
  onSubmit,
  showSubmitButton = false,
  containerStyle,
  isRoundedStyle = false,
  noPadding = false,
  /** Match /models and /decks/recipes catalog search fields (block fill, 10px radius). */
  catalogVariant = false,
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const themeStyles = useMemo(() => getShadowThemeStyles(theme), [theme]);
  const styles = useMemo(
    () => createStyles(colors, isXS, isRoundedStyle, isDark, themeStyles, noPadding, catalogVariant),
    [colors, isXS, isRoundedStyle, isDark, themeStyles, noPadding, catalogVariant],
  );
  const [isFocused, setIsFocused] = useState(false);

  const handleClear = () => {
    onChangeText('');
  };

  const handleKeyPress = useCallback((e) => {
    if (onSubmit && e.nativeEvent.key === 'Enter' && !e.nativeEvent.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }, [onSubmit]);

  const hasValue = value?.trim()?.length > 0;

  return (
    <View style={[styles.searchContainer, containerStyle]}>
      <View style={[
        styles.searchWrapper,
        isFocused && styles.searchWrapperFocused,
        style
      ]}>
        <Icon 
          name={icon} 
          size={14} 
          color={colors.text_color_secondary} 
          style={styles.searchIcon} 
        />
        <TextInput
          {...(Platform.OS === 'macos' ? { enableFocusRing: false } : {})}
          style={styles.searchInput}
          placeholder={placeholder}
          placeholderTextColor={colors.color_input_placeholder}
          value={value}
          onChangeText={onChangeText}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyPress={onSubmit ? handleKeyPress : undefined}
          returnKeyType={onSubmit ? "send" : "default"}
        />
        {showSubmitButton ? (
          <Pressable
            style={[
              styles.submitButton,
              !hasValue && styles.submitButtonDisabled
            ]}
            onPress={onSubmit}
            disabled={!hasValue}
          >
            <Icon 
              name="arrow-up" 
              size={16} 
              color={colors.accent_text_color} 
            />
          </Pressable>
        ) : value ? (
          <TouchableOpacity onPress={handleClear} style={styles.clearButton} hitSlop={catalogVariant ? 6 : undefined}>
            <Icon
              name="xmark"
              size={catalogVariant ? 13 : 14}
              color={catalogVariant ? colors.text_color_tertiary : colors.text_color_secondary}
            />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

export default React.memo(PageSearchInput);
